// components/widgets/WidgetContainer.jsx
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight } from "lucide-react";

const WidgetContainer = ({
  title,
  description,
  intro = null,
  steps = [],
  userId,
  subsectionId,
  onComplete,
  onPhaseChange,
}) => {
  const [started, setStarted] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const phaseRef = useRef(null);

  console.log("WidgetContainer initialized with:", {
    title,
    description,
    intro,
    steps,
    userId,
    subsectionId,
  });

  const hasIntro = !!intro;
  const hasSteps = Array.isArray(steps) && steps.length > 0;

  /* ---------------- Phase sync (intro / immersive) ---------------- */
  useEffect(() => {
    const newPhase = started ? "immersive" : "intro";
    if (phaseRef.current !== newPhase) {
      phaseRef.current = newPhase;
      onPhaseChange?.(newPhase);
    }
  }, [started, onPhaseChange]);

  /* ---------------- Detect full-screen step ---------------- */
  useEffect(() => {
    if (started && steps[stepIndex]?.fullScreen) {
      if (phaseRef.current !== "full") {
        phaseRef.current = "full";
        onPhaseChange?.("full");
      }
    } else if (
      started &&
      phaseRef.current === "full" &&
      !steps[stepIndex]?.fullScreen
    ) {
      // revert to immersive when leaving full-screen step
      phaseRef.current = "immersive";
      onPhaseChange?.("immersive");
    }
  }, [started, stepIndex, steps, onPhaseChange]);

  /* ---------------- Handlers ---------------- */
  const handleStart = () => {
    setStarted(true);
    phaseRef.current = "immersive";
    onPhaseChange?.("immersive");
  };

  const handleNextStep = () => {
    if (stepIndex < steps.length - 1) {
      setStepIndex((prev) => prev + 1);
      console.log("Advancing to step:", stepIndex + 1);
    } else {
      onComplete(); // final step done
      setStarted(false);
      setStepIndex(0);
      phaseRef.current = "intro";
      onPhaseChange?.("intro");
      console.log("Widget completed.");
    }
  };

  const handlePreviousStep = () => {
    if (stepIndex > 0) {
      setStepIndex((prev) => prev - 1);
    } else if (hasIntro) {
      setStarted(false);
      phaseRef.current = "intro";
      onPhaseChange?.("intro");
    }
  };

  /* ---------------- Render ---------------- */
  if (!hasIntro && !hasSteps) {
    // fallback mode (legacy, single child)
    return (
      <div className="relative bg-white/80 backdrop-blur-md rounded-2xl w-full flex-1 flex flex-col">
        <div className="p-10 md:p-12 flex-1 flex flex-col">
          {title && (
            <h2 className="font-medium text-3xl 2xl:text-[32px] leading-snug tracking-normal mb-4">
              {title}
            </h2>
          )}
          {description && (
            <p className="text-[#4B5563] text-base leading-relaxed mb-6">
              {description}
            </p>
          )}
        </div>
      </div>
    );
  }

  const { component: CurrentStep, props: stepProps = {} } = steps[stepIndex] || {};

  return (
    <div className="relative bg-white/80 overflow-y-scroll scrollbar-hide backdrop-blur-md rounded-2xl w-full flex-1 flex flex-col overflow-hidden">
      <AnimatePresence mode="wait">
        {!started && hasIntro ? (
          /* -------- Intro Page -------- */
          <motion.div
            key="intro"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col items-center justify-center text-center p-10 md:p-16"
          >
            {intro.image && (
              <motion.img
                src={intro.image}
                alt="Intro Visual"
                className="w-64 h-auto mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              />
            )}
            {intro.title && (
              <h2 className="text-2xl md:text-3xl font-semibold mb-4 text-gray-800">
                {intro.title}
              </h2>
            )}
            {intro.description && (
              <p className="text-gray-600 max-w-xl mx-auto leading-relaxed mb-8">
                {intro.description}
              </p>
            )}
            <button
              onClick={handleStart}
              className="flex items-center justify-between w-[275px] h-[69px] bg-[#54B0AF] hover:bg-teal-600 text-white font-medium rounded-[20px] px-[20px] py-[22px] shadow-sm transition-all"
            >
              <span>{intro.buttonLabel || "Let’s Get Started"}</span>
              <span className="flex items-center justify-center w-8 h-8 bg-white rounded-full">
                <ChevronRight className="w-5 h-5 text-[#54B0AF]" />
              </span>
            </button>
          </motion.div>
        ) : (
          /* -------- Immersive / Full Step -------- */
          <motion.div
            key={`step-${stepIndex}`}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.3 }}
            className={`flex-1 flex flex-col ${
              steps[stepIndex]?.fullScreen
                ? "p-0 w-screen bg-white overflow-y-auto" // Full-screen layout
                : "p-8 md:p-5 2xl:p-12"
            }`}
          >
            <div className="w-full h-full">
              {/* Step Title */}
              {steps[stepIndex]?.title && !steps[stepIndex]?.fullScreen && (
                <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                  {steps[stepIndex].title}
                </h2>
              )}

              {/* Step Description */}
              {steps[stepIndex]?.description &&
                !steps[stepIndex]?.fullScreen && (
                  <p className="text-gray-600 text-base mb-6 leading-relaxed max-w-2xl">
                    {steps[stepIndex].description}
                  </p>
                )}

              {/* Render Step Component */}
              {CurrentStep && (
                <CurrentStep
                  {...stepProps}
                  userId={userId}
                  subsectionId={subsectionId}
                  onNextStep={handleNextStep}
                  onPreviousStep={handlePreviousStep}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WidgetContainer;